"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { cmsMediaUrl, type Testimonial } from "@/lib/cms"
import {
  StarIcon,
  GoogleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@/components/ui/icons"

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m18!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m7!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp"

/** Cards visible at once — one on mobile, three from `md` up. */
const PER_VIEW_DESKTOP = 3

/**
 * Testimonials as a row of white cards (three up on desktop, one per view on
 * mobile), paged by a scroll-snap slider with dots + arrows once there are
 * more reviews than fit on screen.
 *
 * Experiment (2026-07-29): rendered directly below `CyclingTestimonialBar` on
 * the test detail pages so the dark single-quote bar and this card row can be
 * compared side by side. One of the two comes out before this ships.
 *
 * Paging is driven by native scroll + `scroll-snap` rather than mounting and
 * unmounting slides, so there's no `AnimatePresence` here and nothing that
 * could bail the page to CSR (see `/animation-cache-trap`).
 */
export function TestimonialCardsRow({
  customerCountLabel,
  cmsTestimonials,
}: {
  customerCountLabel: string
  cmsTestimonials?: Testimonial[]
}) {
  // Same normalization as CyclingTestimonialBar: CMS text reviews only, with
  // the review text living in `headline` + optional longer `quote`.
  const reviews = useMemo(
    () =>
      (cmsTestimonials ?? [])
        .filter((c) => c.type === "text")
        .map((c) => ({
          id: c.id,
          quote: [c.headline, c.quote].filter(Boolean).join(" ").trim(),
          author: c.authorName,
          avatarSrc: c.avatar?.url ? cmsMediaUrl(c.avatar.url) : undefined,
          source: c.source,
          rating: c.rating ?? 5,
        }))
        .filter((r) => r.quote),
    [cmsTestimonials]
  )

  const scrollRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLElement | null)[]>([])
  const [perView, setPerView] = useState(1)
  const [page, setPage] = useState(0)

  const pageCount = Math.max(1, Math.ceil(reviews.length / perView))

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    const sync = () => setPerView(mq.matches ? PER_VIEW_DESKTOP : 1)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  // Derive the active page from scroll position: whichever card sits closest
  // to the container's left edge is the first card of the current page.
  const syncPage = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    let nearest = 0
    let best = Infinity
    cardRefs.current.forEach((card, i) => {
      if (!card) return
      const delta = Math.abs(card.offsetLeft - el.scrollLeft)
      if (delta < best) {
        best = delta
        nearest = i
      }
    })
    setPage(Math.floor(nearest / perView))
  }, [perView])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let raf = 0
    const onScroll = () => {
      if (raf !== 0) return
      raf = requestAnimationFrame(() => {
        raf = 0
        syncPage()
      })
    }
    el.addEventListener("scroll", onScroll, { passive: true })
    syncPage()
    return () => {
      el.removeEventListener("scroll", onScroll)
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [syncPage])

  const goToPage = (p: number) => {
    const el = scrollRef.current
    const card = cardRefs.current[p * perView]
    if (!el || !card) return
    el.scrollTo({ left: card.offsetLeft, behavior: "smooth" })
  }

  if (reviews.length === 0) return null

  const showControls = reviews.length > perView

  return (
    <section className="py-16 md:py-20">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        {/* Social-proof header */}
        <div className="flex flex-col items-center gap-2">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5"
          >
            <GoogleIcon width={16} height={16} />
            {Array.from({ length: 5 }, (_, i) => (
              <StarIcon key={i} width={16} height={16} />
            ))}
          </a>
          <p className="type-body-lg text-neutral-600">{customerCountLabel}</p>
        </div>

        {/* Cards — a scroll-snap slider that happens to show three at a time */}
        <div
          ref={scrollRef}
          className="hide-scrollbar mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth"
        >
          {reviews.map((review, i) => (
            <article
              key={review.id}
              ref={(node) => {
                cardRefs.current[i] = node
              }}
              className="flex min-w-full snap-start flex-col justify-center rounded-[16px] bg-white p-8 text-center md:min-w-[calc((100%-2.5rem)/3)]"
            >
              <div className="flex justify-center gap-1">
                {Array.from({ length: review.rating }, (_, s) => (
                  <StarIcon key={s} width={14} height={14} />
                ))}
              </div>
              <p className="type-body-lg mt-5 line-clamp-6 text-neutral-700">
                &ldquo;{review.quote}&rdquo;
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                {review.avatarSrc && (
                  <div className="relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-neutral-100">
                    <Image
                      src={review.avatarSrc}
                      alt=""
                      fill
                      sizes="32px"
                      className="object-cover"
                    />
                  </div>
                )}
                <p className="type-body-md text-neutral-500">
                  {review.author} ·{" "}
                  {review.source === "google" ? "Google" : "Trustpilot"}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Dots + arrows — only once there's more than one page of reviews */}
        {showControls && (
          <div className="mt-8 flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={() => goToPage(Math.max(0, page - 1))}
              disabled={page === 0}
              aria-label="Previous reviews"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-colors hover:border-neutral-900 hover:text-neutral-900 disabled:cursor-default disabled:opacity-30 disabled:hover:border-neutral-300 disabled:hover:text-neutral-600"
            >
              <ChevronLeftIcon width={16} height={16} />
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: pageCount }, (_, p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goToPage(p)}
                  aria-label={`Go to reviews page ${p + 1}`}
                  aria-current={p === page}
                  className={cn(
                    "h-2 cursor-pointer rounded-full transition-all",
                    p === page
                      ? "w-6 bg-neutral-900"
                      : "w-2 bg-neutral-300 hover:bg-neutral-400"
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => goToPage(Math.min(pageCount - 1, page + 1))}
              disabled={page === pageCount - 1}
              aria-label="Next reviews"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-colors hover:border-neutral-900 hover:text-neutral-900 disabled:cursor-default disabled:opacity-30 disabled:hover:border-neutral-300 disabled:hover:text-neutral-600"
            >
              <ChevronRightIcon width={16} height={16} />
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
